<template>
  <a-config-provider :theme="themeConfig">
    <a-layout class="app-layout">
      <a-layout-sider 
        v-model:collapsed="collapsed" 
        collapsible 
        v-if="showSidebar"
        class="app-sider"
        width="240"
      >
        <div class="logo-container">
          <div class="logo-mark">
            <mail-outlined style="color: white; font-size: 16px;" />
          </div>
          <span class="logo-text" v-if="!collapsed">管理控制台</span>
        </div>
        <a-menu 
          v-model:selectedKeys="selectedKeys" 
          theme="dark" 
          mode="inline"
          class="app-menu"
        >
          <a-menu-item key="dashboard" @click="$router.push('/')">
            <dashboard-outlined />
            <span>邮箱管理</span>
          </a-menu-item>
          <a-menu-item key="admins" @click="$router.push('/admins')">
            <user-outlined />
            <span>管理员管理</span>
          </a-menu-item>
          <div class="menu-divider"></div>
          <a-menu-item key="logout" @click="handleLogout" class="logout-item">
            <logout-outlined />
            <span>退出登录</span>
          </a-menu-item>
        </a-menu>
      </a-layout-sider>
      <a-layout class="app-main">
        <a-layout-header class="app-header" v-if="showSidebar">
          <div class="header-content">
            <a-breadcrumb class="app-breadcrumb">
              <a-breadcrumb-item>首页</a-breadcrumb-item>
              <a-breadcrumb-item>{{ currentRouteName }}</a-breadcrumb-item>
            </a-breadcrumb>
            <div class="header-actions">
              <div class="user-badge">
                <user-outlined />
                <span>管理员</span>
              </div>
            </div>
          </div>
        </a-layout-header>
        <a-layout-content class="app-content">
          <router-view v-slot="{ Component }">
            <transition name="fade-slide" mode="out-in">
              <component :is="Component" />
            </transition>
          </router-view>
        </a-layout-content>
      </a-layout>
    </a-layout>
  </a-config-provider>
</template>

<script>
import { ref, watch, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import {
  DashboardOutlined,
  UserOutlined,
  LogoutOutlined,
  MailOutlined,
} from '@ant-design/icons-vue';

export default {
  name: 'App',
  components: {
    DashboardOutlined,
    UserOutlined,
    LogoutOutlined,
    MailOutlined,
  },
  setup() {
    const collapsed = ref(false);
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const selectedKeys = ref(['dashboard']);

    const showSidebar = computed(() => route.path !== '/login');
    
    const currentRouteName = computed(() => {
      if (route.path === '/') return '邮箱管理';
      if (route.path === '/admins') return '管理员管理';
      return '';
    });

    watch(() => route.path, (path) => {
      if (path === '/') selectedKeys.value = ['dashboard'];
      if (path === '/admins') selectedKeys.value = ['admins'];
    }, { immediate: true });

    const handleLogout = () => {
      store.commit('setToken', '');
      localStorage.removeItem('token');
      router.push('/login');
    };

    const themeConfig = {
      token: {
        colorPrimary: '#3b82f6', // Blue
        colorSuccess: '#10B981',
        colorWarning: '#F59E0B',
        colorError: '#EF4444',
        colorInfo: '#3B82F6',
        colorTextBase: '#1E293B', // Slate 800
        borderRadius: 4,
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      },
      components: {
        Layout: {
          colorBgHeader: '#ffffff',
          colorBgBody: '#F8FAFC', // Slate 50
        },
        Menu: {
          colorItemBg: 'transparent',
          colorItemText: '#94A3B8', // Slate 400
          colorItemTextHover: '#F8FAFC',
          colorItemTextSelected: '#ffffff',
          colorItemBgSelected: 'rgba(255, 255, 255, 0.1)',
        }
      }
    };

    return {
      collapsed,
      selectedKeys,
      showSidebar,
      currentRouteName,
      handleLogout,
      themeConfig
    };
  }
};
</script>

<style>
/* Global Styles & Variables */
:root {
  --color-navy: #0F172A;
  --color-navy-light: #1E293B;
  --color-slate: #64748B;
  --color-slate-light: #94A3B8;
  --color-primary: #3b82f6;
  --color-primary-light: #60a5fa;
  --color-bg: #F8FAFC;
  --color-surface: #FFFFFF;
  --color-border: #E2E8F0;
  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  background-color: var(--color-bg);
}

#app {
  height: 100%;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Layout */
.app-layout {
  min-height: 100vh;
  background-color: var(--color-bg);
}

/* Sidebar */
.app-sider {
  background-color: var(--color-navy) !important;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  z-index: 10;
}
...
/* Content */
.app-content {
  padding: 24px;
  position: relative;
  background-color: var(--color-bg);
}

#app {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Layout */
.app-layout {
  min-height: 100vh;
  background-color: var(--color-bg);
}

/* Sidebar */
.app-sider {
  background-color: var(--color-navy) !important;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  z-index: 10;
}

.logo-container {
  height: 64px;
  display: flex;
  align-items: center;
  padding: 0 24px;
  gap: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  overflow: hidden;
}

.logo-mark {
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  border-radius: 4px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.logo-text {
  color: #ffffff;
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 1px;
  white-space: nowrap;
}

.app-menu {
  background-color: transparent !important;
  border-right: none !important;
  padding: 16px 8px;
}

.ant-menu-item {
  border-radius: 6px !important;
  margin-bottom: 8px !important;
}

.menu-divider {
  height: 1px;
  background-color: rgba(255, 255, 255, 0.05);
  margin: 16px 8px;
}

.logout-item {
  margin-top: auto;
}

.logout-item:hover {
  color: #EF4444 !important;
  background-color: rgba(239, 68, 68, 0.1) !important;
}

/* Header */
.app-header {
  background-color: var(--color-surface) !important;
  padding: 0 32px !important;
  height: 64px;
  line-height: 64px;
  box-shadow: var(--shadow-sm);
  z-index: 5;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
}

.app-breadcrumb {
  font-size: 14px;
}

.user-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-slate);
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 20px;
  background-color: var(--color-bg);
  border: 1px solid var(--color-border);
  cursor: pointer;
  transition: all 0.2s;
}

.user-badge:hover {
  background-color: var(--color-surface);
  border-color: var(--color-slate-light);
  color: var(--color-navy);
}

/* Content */
.app-content {
  margin: 32px;
  position: relative;
}

/* Transitions */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
